import { useState, useCallback, useRef, useEffect } from 'react';
import { formatCurrency } from "@/lib/portfolioCalculations";
import { toast } from "sonner";
import { updatePortfolioFields } from "@/services/portfolio/PortfolioService";
import { fetchAllAllocations, updateAllocationFields } from "@/services/portfolio/PortfolioAllocationsService";
import { schedulePortfolioStatusRecompute } from "@/utils/portfolioStatusUpdater";
import { useDebouncedCallback } from 'use-debounce';
import { syncAllocationsForPortfolioSizeChange, type AllocationData } from "@/lib/portfolioCalculations";

interface UseDebouncedPortfolioSizeProps {
  portfolioId: string;
  initialSize: number;
  onSuccess?: () => void;
  portfolioType?: string; // 'model' | 'personal' | 'client' (optional)
  currencyCode?: string; // display currency for messages
}

export const useDebouncedPortfolioSize = ({ 
  portfolioId, 
  initialSize, 
  onSuccess,
  portfolioType,
  currencyCode,
}: UseDebouncedPortfolioSizeProps) => {
  const [portfolioSize, setPortfolioSize] = useState(initialSize);
  const [isSaving, setIsSaving] = useState(false);
  const lastSavedSize = useRef(initialSize);
  const typeRef = useRef(portfolioType);
  const currencyRef = useRef(currencyCode);

  useEffect(() => {
    typeRef.current = portfolioType;
  }, [portfolioType]);

  useEffect(() => {
    currencyRef.current = currencyCode;
  }, [currencyCode]);

  // Keep internal state in sync if the source of truth (prop) changes
  // This covers cases where DB value updates externally or after refetch
  useEffect(() => {
    setPortfolioSize(initialSize);
    lastSavedSize.current = initialSize;
  }, [initialSize]);

  // Fetch validation data: locked dollar sum and unlocked percentage sum
  const getShrinkValidationData = useCallback(async (): Promise<{ lockedSum: number; unlockedPctSum: number; } > => {
    try {
      const data = await fetchAllAllocations(portfolioId);
      let lockedSum = 0;
      let unlockedPctSum = 0;
      (data || []).forEach((row: any) => {
        const isLocked = !!row.is_value_locked;
        const val = Number(row.allocation_value);
        const pct = Number(row.allocation_percentage);
        if (isLocked && Number.isFinite(val)) lockedSum += Math.max(0, val);
        if (!isLocked && Number.isFinite(pct)) unlockedPctSum += Math.max(0, pct);
      });
      lockedSum = Math.round(lockedSum * 100) / 100;
      unlockedPctSum = Math.min(100, Math.max(0, Math.round(unlockedPctSum * 100) / 100));
      return { lockedSum, unlockedPctSum };
    } catch (e) {
      console.warn('Validation fetch error:', e);
      return { lockedSum: 0, unlockedPctSum: 0 };
    }
  }, [portfolioId]);

  // Pre-validate new size for dollar-mode portfolios
  const preValidateSize = useCallback(async (newSize: number): Promise<{ ok: boolean; lockedSum?: number; minSize?: number; }> => {
    // Only enforce for non-model portfolios
    if ((typeRef.current || '').toLowerCase() === 'model') return { ok: true };
    if (!Number.isFinite(newSize) || newSize <= 0) return { ok: false };
    const { lockedSum, unlockedPctSum } = await getShrinkValidationData();
    // Minimal feasible size S_min ensures unlocked percentages fit after shrink:
    // S_min = lockedSum / (1 - unlockedPctSum/100)
    let minSize = lockedSum;
    const pctFrac = Math.min(0.99, Math.max(0, unlockedPctSum / 100));
    if (pctFrac < 0.99) {
      minSize = lockedSum / (1 - pctFrac);
    } else {
      // If unlocked == ~100%, any locked dollars make shrink impossible below current locked sum
      minSize = lockedSum;
    }
    minSize = Math.round(minSize * 100) / 100;
    if (newSize + 0.01 < minSize) {
      return { ok: false, lockedSum, minSize };
    }
    return { ok: true, lockedSum, minSize };
  }, [getShrinkValidationData]);

  // Synchronize allocations when portfolio size changes
  const syncAllocationsForSizeChange = useCallback(async (newSize: number) => {
    if (!Number.isFinite(newSize) || newSize <= 0) {
      console.warn('Skipping allocation sync due to non-positive portfolio size:', newSize);
      return;
    }
    try {
      // Determine if we should skip syncing a specific allocation due to a pending oversize/autosize flow
      let skipAllocationId: string | undefined = undefined;
      try {
        const pendingRaw = sessionStorage.getItem('portfolio-pending-oversize');
        const pending = pendingRaw ? JSON.parse(pendingRaw) : null;
        if (pending && pending.expires > Date.now() && pending.allocationId) {
          skipAllocationId = String(pending.allocationId);
        }
      } catch {}

      // Fetch current allocations
      const allocations = await fetchAllAllocations(portfolioId);
      if (!allocations || allocations.length === 0) return;

      // Sync allocations with new portfolio size
      const syncedAllocations = syncAllocationsForPortfolioSizeChange(
        allocations as AllocationData[], 
        newSize
      );

      // Update allocations in database with single-source-of-truth per row:
      // - For locked rows: update allocation_value only (but skip if value is null/undefined to avoid zeroing)
      // - For unlocked rows: update allocation_percentage only
      for (const allocation of syncedAllocations) {
        if (skipAllocationId && String(allocation.id) === skipAllocationId) {
          console.debug('[useDebouncedPortfolioSize] Skipping allocation sync for pending oversize allocation', allocation.id);
          continue;
        }
        const locked = !!allocation.is_value_locked;
        const pct = Math.max(0, Math.min(100, Number(allocation.allocation_percentage) || 0));
        const payload: any = { updated_at: new Date().toISOString() };

        if (locked) {
          // Only send allocation_value if it is explicitly present; avoid writing 0 for null values,
          // which could trigger DB sync to zero out percentages unintentionally.
          if (allocation.allocation_value !== null && allocation.allocation_value !== undefined) {
            payload.allocation_value = allocation.allocation_value;
          } else {
            // Skip updating this row to prevent accidental zeroing when no explicit dollar lock exists
            continue;
          }
        } else {
          // Only send percentage for unlocked rows
          payload.allocation_percentage = pct;
        }

        try {
          await updateAllocationFields(allocation.id, payload);
        } catch (updateError) {
          console.warn('Allocation sync update failed for row', allocation.id, updateError);
        }
      }
    } catch (error) {
      console.error('Error syncing allocations:', error);
      // Don't show toast error for allocation sync - portfolio size update is more important
    }
  }, [portfolioId]);

  // Debounced database update - only fires 500ms after user stops typing
  const debouncedUpdate = useDebouncedCallback(
    async (newSize: number) => {
      // Skip if a programmatic update just occurred to avoid racing old values
      try {
        const until = Number(sessionStorage.getItem('portfolio-size-programmatic') || '0');
        if (until && Date.now() < until) {
          console.debug('[useDebouncedPortfolioSize] Skipping debounced update due to recent programmatic change', { newSize });
          return;
        }
      } catch {}

      console.info('[useDebouncedPortfolioSize] Debounced size update start', { newSize, lastSaved: lastSavedSize.current });
      // Never persist non-positive sizes; this avoids DB triggers recalculating allocations to zero
      if (!Number.isFinite(newSize) || newSize <= 0) {
        return;
      }
      if (newSize === lastSavedSize.current) return;
      
      // Pre-validate to avoid constraint errors for dollar-mode portfolios
      const validation = await preValidateSize(newSize);
      if (!validation.ok) {
        const minSize = validation.minSize ?? validation.lockedSum ?? 0;
        const msgSize = formatCurrency(minSize, currencyRef.current);
        toast.error(`Portfolio size cannot be less than ${msgSize} based on current allocations. Try Auto-size or unlock.`);
        // Suppress overflow dialog for a short time to avoid reversed UX
        try { sessionStorage.setItem('portfolio-overflow-suppress', String(Date.now() + 2000)); } catch {}
        // Revert UI to last saved valid size
        setPortfolioSize(lastSavedSize.current);
        return;
      }

      setIsSaving(true);
      try {
        let updated = false;
        try {
          await updatePortfolioFields(portfolioId, { portfolio_size: newSize });
          updated = true;
        } catch (error) {
          console.error('Error updating portfolio size:', error);
          toast.error("Failed to save portfolio size");
          // Revert to last saved size on error
          setPortfolioSize(lastSavedSize.current);
        }
        if (updated) {
          console.info('[useDebouncedPortfolioSize] Debounced size update success', { newSize });
          lastSavedSize.current = newSize;
          // Sync allocations after successful portfolio size update
          await syncAllocationsForSizeChange(newSize);
          // Debounced recompute of portfolio status based on current allocations/size
          try { schedulePortfolioStatusRecompute(portfolioId); } catch {}
          onSuccess?.();
        }
      } catch (error) {
        console.error('Error updating portfolio size:', error);
        toast.error("Failed to save portfolio size");
        // Revert to last saved size on error
        setPortfolioSize(lastSavedSize.current);
      } finally {
        setIsSaving(false);
      }
    },
    500 // 500ms delay
  );

  const handlePortfolioSizeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    const newSize = Number(value) || 0;
    
    // Update UI immediately (optimistic update)
    setPortfolioSize(newSize);
    
    // Trigger debounced database update
    debouncedUpdate(newSize);
  }, [debouncedUpdate]);

  // Immediate update function for programmatic changes
  const updatePortfolioSize = useCallback(async (newSize: number): Promise<boolean> => {
    console.info('[useDebouncedPortfolioSize] Programmatic size update invoked', { newSize, lastSaved: lastSavedSize.current });
    setPortfolioSize(newSize);
    // If the size hasn't changed, skip DB write and allocation sync to avoid unintended side-effects
    if (Number.isFinite(newSize) && newSize === lastSavedSize.current) {
      console.info('[useDebouncedPortfolioSize] Programmatic update skipped (no change)');
      return true;
    }
    if (!Number.isFinite(newSize) || newSize <= 0) {
      toast.error("Portfolio size must be greater than zero");
      setPortfolioSize(lastSavedSize.current);
      return false;
    }

    // Pre-validate to avoid constraint errors for dollar-mode portfolios
    const validation = await preValidateSize(newSize);
    if (!validation.ok) {
      const minSize = validation.minSize ?? validation.lockedSum ?? 0;
      const msgSize = formatCurrency(minSize, currencyRef.current);
      toast.error(`Portfolio size cannot be less than ${msgSize} based on current allocations. Try Auto-size or unlock.`);
      try { sessionStorage.setItem('portfolio-overflow-suppress', String(Date.now() + 2000)); } catch {}
      setPortfolioSize(lastSavedSize.current);
      return false;
    }

    // Set short suppression for debounced user updates to avoid racing older values
    try { sessionStorage.setItem('portfolio-size-programmatic', String(Date.now() + 2000)); } catch {}

    setIsSaving(true);
    
    try {
      await updatePortfolioFields(portfolioId, { portfolio_size: newSize });

      console.info('[useDebouncedPortfolioSize] Programmatic size update success', { newSize });
      lastSavedSize.current = newSize;
      // Sync allocations after successful portfolio size update
      await syncAllocationsForSizeChange(newSize);
      // Debounced recompute of portfolio status based on current allocations/size
      try { schedulePortfolioStatusRecompute(portfolioId); } catch {}
      onSuccess?.();
      return true;
    } catch (error) {
      toast.error("Failed to update portfolio size");
      setPortfolioSize(lastSavedSize.current);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [portfolioId, onSuccess, syncAllocationsForSizeChange]);

  return {
    portfolioSize,
    setPortfolioSize,
    handlePortfolioSizeChange,
    updatePortfolioSize,
    isSaving
  };
};
